import React, { useRef, useState, useEffect } from "react";
import { FaPaperclip } from "react-icons/fa";
import { IoSendSharp } from "react-icons/io5";
import { handleUserAssistantPrompt } from "./Gemini/UserAssistantPage";
import { FaShareAlt } from "react-icons/fa";
import { LuCopy } from "react-icons/lu";

import { fetchAiAdsInsight } from "./Gemini/promptHelper";
import autoCorrectPrompt from "./Tools/AutoCorrectPrompt";

// Import the new Tables
import {
  ComparisonSummaryTable,
  DayByDayComparisonTable,
} from "./Gemini/AdsTables";

function PromptPage() {
  const [messages, setMessages] = useState([]);
  const [userPrompt, setUserPrompt] = useState("");
  const [typingMessage, setTypingMessage] = useState(null);

  // 1. STATE FOR DROPDOWN
  const [promptType, setPromptType] = useState("overall");
  const contentRef = useRef(null);

  // Auto scroll
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [messages, typingMessage]);
  const fetchByModel = async (text, type) => {
    switch (type) {
      case "user_assistant":
        return await handleUserAssistantPrompt(text);

      case "day":
        return await fetchAiAdsInsight(text, "day");

      case "overall":
      default:
        return await fetchAiAdsInsight(text, "overall");
    }
  };

  const addPromptButton = async () => {
    if (!userPrompt.trim()) return;

    // Capture the current type at the moment of sending
    const currentType = promptType;
    const uiLabelMap = {
      overall: "Overall: ",
      day: "Day-by-day: ",
      user_assistant: "User Assistant: ",
    };

    // UI Label
    const uiLabel = uiLabelMap[currentType] || "";

    // Add User Message
    const userMsgObj = {
      id: Date.now(),
      from: "user",
      content: `${uiLabel}${userPrompt}`,
      type: "text",
    };
    setMessages((prev) => [...prev, userMsgObj]);
    setUserPrompt("");

    // Autocorrect
    const correctedPrompt = await autoCorrectPrompt(`${uiLabel}${userPrompt}`);

    // Call API
    await handleGptFetch(correctedPrompt, currentType);
  };

  const handleGptFetch = async (text, type) => {
    const loadingId = Date.now();
    setMessages((prev) => [
      ...prev,
      { id: loadingId, from: "gpt", loading: true },
    ]);

    const result = await fetchByModel(text, type);

    // Remove loading AFTER result arrives
    setMessages((prev) => prev.filter((msg) => msg.id !== loadingId));

    if (!result) return;

    if (result.type === "text") {
      simulateTypingEffect(result.content);
    }

    if (result.type === "data_viz") {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          from: "gpt",
          type: "data_viz",
          vizType: result.vizType || type,
          data: result.data,
        },
      ]);
    }
  };

  const simulateTypingEffect = (text) => {
    // ... existing typing logic ...
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), from: "gpt", type: "text", content: text },
    ]);
  };

  // 3. RENDER CONTENT BASED ON TYPE
  const renderMessageContent = (msg) => {
    if (msg.loading)
      return <div className="text-blue-500 animate-pulse">Thinking...</div>;

    if (msg.type === "text") {
      return (
        <div className="space-y-3">
          <p>{msg.content}</p>

          {msg.meta?.model === "user_assistant" && (
            <div className="mt-3 p-4 rounded-xl bg-gray-50 border text-sm">
              <div>
                <strong>Total Cost:</strong> ₹{msg.meta.total_cost}
              </div>
              <div>
                <strong>Total Clicks:</strong> {msg.meta.total_clicks}
              </div>
              <div>
                <strong>Date Range:</strong> {msg.meta.date_range.start_date} →{" "}
                {msg.meta.date_range.end_date}
              </div>
            </div>
          )}
        </div>
      );
    }

    if (msg.type === "data_viz") {
      // IF DAY -> Show Day Table
      if (msg.vizType === "day") {
        return <DayByDayComparisonTable data={msg.data} />;
      }
      // ELSE -> Show Overall Table
      return <ComparisonSummaryTable data={msg.data} />;
    }
  };
  const generateHtmlTable = (data) => {
    if (!data || data.length === 0) return "";

    const headers = [
      "Period",
      "Date",
      "Impressions",
      "Clicks",
      "CTR",
      "Cost",
      "Conversions",
    ];

    let rows = "";

    data.forEach((row) => {
      rows += `
      <tr>
        <td>${row.period_type ?? ""}</td>
        <td>${row.date ?? ""}</td>
        <td>${row.impressions ?? 0}</td>
        <td>${row.clicks ?? 0}</td>
        <td>${row.ctr ?? 0}%</td>
        <td>₹${row.cost ?? 0}</td>
        <td>${row.conversions ?? 0}</td>
      </tr>
    `;
    });

    return `
    <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; font-family: Arial; font-size: 14px;">
      <thead style="background-color:#2e38b5;color:white;">
        <tr>
          ${headers.map((h) => `<th>${h}</th>`).join("")}
        </tr>
      </thead>
      <tbody>
        ${rows}
      </tbody>
    </table>
  `;
  };

  const handleCopy = async (item) => {
    try {
      let htmlContent = "";

      // If it's normal text
      if (item.type === "text") {
        htmlContent = `<div>${item.content}</div>`;
      }

      // If it's data table
      if (item.type === "data_viz" && Array.isArray(item.data)) {
        htmlContent = generateHtmlTable(item.data);
      }

      await navigator.clipboard.write([
        new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([htmlContent], { type: "text/plain" }),
        }),
      ]);

      alert("Copied as formatted table!");
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  const handleShare = async (item) => {
    let textToShare = "";

    if (item.type === "text") {
      textToShare = item.content;
    }

    if (item.type === "data_viz") {
      textToShare = JSON.stringify(item.data, null, 2);
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: "AI Ads Insight",
          text: textToShare,
        });
      } catch (err) {
        console.error("Share failed", err);
      }
    } else {
      alert("Sharing not supported on this browser.");
    }
  };

  return (
    <div className="m-4 h-[95%] bg-white">
      <div className="mx-28 my-2 h-5/6 flex flex-col justify-between">
        {/* Chat Body */}
        <div className="w-full p-2 pt-3 h-full overflow-auto" ref={contentRef}>
          {messages.map((item) => (
            <div
              key={item.id}
              className={`p-2 flex ${item.from === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`p-3 px-5 rounded-2xl max-w-4xl relative ${
                  item.from === "user"
                    ? "bg-gray-100"
                    : "bg-white shadow-md border"
                }`}
              >
                {renderMessageContent(item)}

                {/* COPY + SHARE (Only for GPT responses) */}
                {item.from === "gpt" && !item.loading && (
                  <div className="flex justify-end gap-3 mt-3 pt-3 border-t">
                    <button
                      onClick={() => handleCopy(item)}
                      className="text-xs px-3 py-1 rounded-md bg-gray-100 hover:bg-gray-200"
                    >
                      <LuCopy className="ml-2 text-lg" />
                    </button>

                    <button
                      onClick={() => handleShare(item)}
                      className="text-xs px-3 py-1 rounded-md text-blue-700 text-blue hover:opacity-90"
                    >
                      <FaShareAlt className="ml-2 text-lg" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Input Area */}
        <div className="w-full relative flex items-center justify-center mt-4">
          {/* 4. DROPDOWN SELECTION */}
          <select
            value={promptType}
            onChange={(e) => setPromptType(e.target.value)}
            className="absolute left-12 bg-gray-50 border text-sm font-semibold rounded-full px-3 py-2"
          >
            <option value="overall">Overall</option>
            <option value="day">Day-by-Day</option>
            <option value="user_assistant">User Assistant</option>
          </select>

          <button className="absolute right-2 p-3 bg-[#4e47e5] text-white rounded-full">
            <FaPaperclip />
          </button>

          <input
            type="text"
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addPromptButton()}
            placeholder="Ask something..."
            className="w-full border-2 border-gray-200 pl-48 pr-16 py-4 rounded-full focus:outline-none focus:border-blue-500"
          />

          <button
            onClick={addPromptButton}
            className="absolute right-2 p-3 bg-[#4e47e5] text-white rounded-full"
          >
            <IoSendSharp />
          </button>
        </div>
      </div>
    </div>
  );
}

export default PromptPage;
